import { API } from "@aws-amplify/api";
import { API_REKOGNITION } from "../../constants";

const rekognitionAPI = API_REKOGNITION;
const faceMatchPath = "/facematch";
const detectAttributesPath = "/detectattributes";

/**
 * Search for a matching face in AWS Rekognition collection
 * Collection ID is configured in the backend Lambda environment
 * @param {string} imageDataUrl - Base64 encoded image data URL (optional if imageS3Key provided)
 * @param {number} faceMatchThreshold - Optional threshold (0-100) for face matching confidence
 * @param {number} maxFaces - Optional max number of faces to return (1-20)
 * @param {string} imageS3Key - Optional S3 key of uploaded image (alternative to base64)
 * @param {string} userId - Optional Cognito sub ID (required if using imageS3Key)
 * @returns {Promise<Object>} Match result with face details, similarity score, and organized S3 key
 */
export async function searchFace(imageDataUrl, faceMatchThreshold, maxFaces, imageS3Key = null, userId = null) {
  try {
    const body = {};

    // Use S3 key if provided, otherwise use base64
    if (imageS3Key) {
      body.imageS3Key = imageS3Key;
      body.userId = userId;
    } else if (imageDataUrl) {
      body.image = imageDataUrl;
    } else {
      throw new Error('Either imageDataUrl or imageS3Key must be provided');
    }

    if (faceMatchThreshold !== undefined) {
      body.faceMatchThreshold = faceMatchThreshold;
    }
    if (maxFaces !== undefined) {
      body.maxFaces = maxFaces;
    }

    const response = await API.post(rekognitionAPI, faceMatchPath, {
      body
    });

    return response;
  } catch (error) {
    console.error("Face match API call failed:", error);
    throw error;
  }
}

/**
 * Detect facial attributes from an image using AWS Rekognition
 * Returns attributes mapped to application enum format
 * @param {string} imageDataUrl - Base64 encoded image data URL
 * @returns {Promise<Object>} Detected facial attributes for all faces in image
 */
export async function detectFaceAttributes(imageDataUrl) {
  try {
    const body = { image: imageDataUrl };

    const response = await API.post(rekognitionAPI, detectAttributesPath, {
      body
    });

    return response;
  } catch (error) {
    console.error("Face attributes detection failed:", error);
    throw error;
  }
}
